import { useTranslation } from 'react-i18next';
import { AlertCircle } from 'lucide-react';
import {
  PAYOUT_COUNTRIES, countryMismatch, isPayoutCountry, payoutCountryLabelKey,
  payoutCountryTextKey, type PayoutCountry,
} from '@/lib/payoutCountry';
import formStyles from '@/components/Form.module.css';
import styles from './PayoutCountryField.module.css';

/**
 * The bank account country for a NEW Stripe Express account (item 2,
 * 2026-10-02), on the pattern of the admin LocationBankCard: a select that
 * starts empty ("Choose country", then United States and Canada), the
 * cannot-change warning, and a confirmation the host ticks once a country
 * is chosen. Rendered only before an account exists; an existing account's
 * country is read-only on ConnectStatusCard.
 *
 * Changing the country clears the confirmation, so a tick given for one
 * country never carries over to the other. When the host's home is known
 * to be in the other country, a warning says so: never a block, since a
 * host can live and bank in one country and own a home in the other.
 *
 * Controlled; the caller holds `country` and `confirmed` and gates its
 * Stripe button on canStartPayout. `updating` (the Api does not take a
 * country yet, apiTakesCountry) adds a line saying why that button waits.
 */
export interface PayoutCountryFieldProps {
  /** Distinct per screen, for the label and checkbox ids. */
  idPrefix: string;
  country: PayoutCountry | '';
  confirmed: boolean;
  onChange: (next: { country: PayoutCountry | ''; confirmed: boolean }) => void;
  /** The country of the host's home, when known (GET /me/connect/status). */
  homeCountry?: PayoutCountry | null;
  /** True while the Api answering is one that would ignore the country. */
  updating?: boolean;
  disabled?: boolean;
}

export function PayoutCountryField({
  idPrefix, country, confirmed, onChange, homeCountry, updating = false, disabled = false,
}: PayoutCountryFieldProps) {
  const { t } = useTranslation('connect');
  const selectId = `${idPrefix}-payout-country`;
  const mismatch = country !== '' && countryMismatch(country, homeCountry ?? null);

  return (
    <div className={styles.fields}>
      <div className={formStyles.field}>
        <label htmlFor={selectId} className={formStyles.label}>{t('country.label')}</label>
        <select
          id={selectId}
          className={formStyles.input}
          value={country}
          onChange={(e) => {
            const next = e.target.value;
            onChange({ country: isPayoutCountry(next) ? next : '', confirmed: false });
          }}
          disabled={disabled}
        >
          <option value="">{t('country.choose')}</option>
          {PAYOUT_COUNTRIES.map((c) => (
            <option key={c} value={c}>{t(payoutCountryLabelKey(c))}</option>
          ))}
        </select>
        <p className={styles.warning}>{t('country.warning')}</p>
      </div>

      {mismatch && homeCountry && (
        <p className={`${styles.mismatch} ${styles.reveal}`} role="status">
          <AlertCircle size={14} aria-hidden className={styles.mismatchIcon} />
          <span>
            {t('country.mismatchChoice', {
              home: t(payoutCountryTextKey(homeCountry)),
              country: t(payoutCountryTextKey(country)),
            })}
          </span>
        </p>
      )}

      {country !== '' && (
        <label className={`${styles.confirmRow} ${styles.reveal}`} htmlFor={`${idPrefix}-payout-confirm`}>
          <input
            id={`${idPrefix}-payout-confirm`}
            type="checkbox"
            checked={confirmed}
            onChange={(e) => onChange({ country, confirmed: e.target.checked })}
            disabled={disabled}
          />
          <span>{t('country.confirm', { country: t(payoutCountryTextKey(country)) })}</span>
        </label>
      )}

      {updating && (
        <p className={styles.warning} role="status">{t('country.updating')}</p>
      )}
    </div>
  );
}
